import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, UserIcon } from '@heroicons/react/outline';
import { createPortal } from 'react-dom';
import useFixedOverlayPosition from './useFixedOverlayPosition';

function initials(user) {
  const first = user?.firstName?.trim()?.[0] || '';
  const last = user?.lastName?.trim()?.[0] || '';
  const init = (first + last).toUpperCase();
  return init || (user?.email?.trim()?.[0] || '?').toUpperCase();
}

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return name || user?.email || user?.id || 'Unknown';
}

function ToDoAssigneeDropdown({ value, users, onChange, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef();
  const menuRef = useRef();
  const pos = useFixedOverlayPosition(anchorRef, open);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (anchor?.contains(e.target) || menu?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selected = useMemo(() => users.find((u) => u.id === value), [users, value]);
  const ordered = useMemo(() => {
    return [...users].sort((a, b) => displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' }));
  }, [users]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={anchorRef}
        className={
          'w-full border rounded-lg px-2 py-1.5 text-sm flex items-center gap-2 justify-between ' +
          (disabled ? 'opacity-60 cursor-not-allowed bg-neutral-50' : 'bg-white hover:bg-slate-50')
        }
        onClick={() => !disabled && setOpen((p) => !p)}
        type="button"
        disabled={disabled}
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected ? (
            <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-semibold shrink-0">
              {initials(selected)}
            </span>
          ) : (
            <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
              <UserIcon className="w-4 h-4" />
            </span>
          )}
          <span className="truncate">{selected ? displayName(selected) : 'Unassigned'}</span>
        </span>
        <ChevronDownIcon className="w-5 h-5 shrink-0" />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[9999] bg-white shadow-basic rounded-lg border p-1 max-h-64 overflow-auto"
              style={{
                left: pos.left,
                width: pos.width,
                top: pos.top,
                bottom: pos.bottom,
              }}
            >
              <button
                className={
                  'w-full text-left px-2 py-2 rounded-md flex items-center gap-2 text-sm hover:bg-slate-100 ' +
                  (!value ? 'bg-slate-900 text-white hover:bg-slate-900' : '')
                }
                type="button"
                onClick={() => {
                  onChange?.(null);
                  setOpen(false);
                }}
              >
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                  <UserIcon className="w-4 h-4" />
                </span>
                <span className="truncate">Unassigned</span>
              </button>

              {loading ? (
                <div className="px-2 py-2 text-sm text-neutral-500">Loading…</div>
              ) : ordered.length === 0 ? (
                <div className="px-2 py-2 text-sm text-neutral-500">No users</div>
              ) : (
                ordered.map((u) => (
                  <button
                    key={u.id}
                    className={
                      'w-full text-left px-2 py-2 rounded-md flex items-center gap-2 text-sm hover:bg-slate-100 ' +
                      (u.id === value ? 'bg-slate-900 text-white hover:bg-slate-900' : '')
                    }
                    type="button"
                    onClick={() => {
                      onChange?.(u.id);
                      setOpen(false);
                    }}
                  >
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-semibold shrink-0">
                      {initials(u)}
                    </span>
                    <span className="truncate">{displayName(u)}</span>
                  </button>
                ))
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default ToDoAssigneeDropdown;
